import { Controller } from "@hotwired/stimulus"

// Holder modtagertallet og prisoverslaget i takt med formularen, mens
// beskeden skrives.
//
// Den henter overslaget frem for at regne det ud her. Antallet af SMS'er per
// besked afhænger af GSM-03.38-alfabetet, og af afmeldingslinjen, der lægges
// på bagefter; de to regler hører ét sted hjemme, og det sted er SmsText på
// serveren. To kopier af den slags regel bliver før eller siden uenige, og
// den uenighed ville være et prisoverslag, der ikke passer.
//
// Modtagerlisten hentes for sig, når siden åbner, og igen kun, når et filter
// skifter. Den er personoplysninger og skrives i handlingsloggen ved hvert
// opslag, og den må ikke skiftes ud, mens nogen står og sætter flueben i den.
//
// Begge opslag er et POST med formularen i kroppen og ikke et GET med den i
// adressen. Hvert medlem på listen er to felter, og ved et par hundrede
// medlemmer bliver adressen længere, end serveren vil tage imod; så kom der
// intet svar, og listen blev stående, som den var.
export default class extends Controller {
  static values = { url: String, recipientsUrl: String, testUrl: String, delay: { type: Number, default: 400 } }
  static targets = ["frame", "recipients", "recipient", "testButton", "testStatus"]

  connect() {
    this.controller = null
    this.recipientsController = null
    this.reloadRecipients()
  }

  disconnect() {
    clearTimeout(this.timeout)
    this.controller?.abort()
    this.recipientsController?.abort()
  }

  refresh() {
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this.load(), this.delayValue)
  }

  async reloadRecipients() {
    if (!this.hasRecipientsTarget) return

    this.recipientsController?.abort()
    this.recipientsController = new AbortController()

    const body = this.formData()
    body.delete("broadcast[body]")
    body.delete("broadcast[subject]")

    const html = await this.post(this.recipientsUrlValue, body, this.recipientsController)
    if (html !== null) {
      this.recipientsTarget.innerHTML = html
      this.refresh()
    }
  }

  // Prøven til den, der er logget ind, med det, formularen står med lige nu.
  // Knappen er slået fra, mens den sendes, så et dobbelttryk ikke bliver to
  // SMS'er. Serveren svarer med en besked, også når prøven ikke blev sendt.
  async sendTest() {
    this.testButtonTarget.disabled = true
    this.testStatusTarget.textContent = "Sender prøven…"

    try {
      const html = await this.post(this.testUrlValue, this.formData(), new AbortController())
      this.testStatusTarget.innerHTML = html ?? "<p class='ad-note ad-note--danger'>Prøven blev ikke sendt. Prøv igen om lidt.</p>"
    } catch {
      this.testStatusTarget.innerHTML = "<p class='ad-note ad-note--danger'>Prøven blev ikke sendt, fordi forbindelsen svigtede. Prøv igen om lidt.</p>"
    } finally {
      this.testButtonTarget.disabled = false
    }
  }

  chooseAll() {
    this.choose(true)
  }

  chooseNone() {
    this.choose(false)
  }

  choose(checked) {
    this.recipientTargets.forEach((box) => { box.checked = checked })
    this.refresh()
  }

  // Formularens felter. _method tages af: på en kladde, der rettes, står der
  // "patch", og så ville opslaget blive læst som en rettelse. Formularens
  // nøgle mod CSRF tages også af: den gælder kun formularens egen adresse, og
  // her afviste Rails den med 422. Sidens fælles nøgle går med i headeren.
  formData() {
    const data = new FormData(this.element)
    data.delete("_method")
    data.delete("authenticity_token")
    return data
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content
  }

  async load() {
    this.controller?.abort()
    this.controller = new AbortController()

    const html = await this.post(this.urlValue, this.formData(), this.controller)
    if (html !== null) this.frameTarget.innerHTML = html
  }

  // Svaret som tekst, eller null, når opslaget blev afbrudt eller gik galt.
  async post(url, body, controller) {
    try {
      const response = await fetch(url, {
        method: "POST",
        body,
        headers: { Accept: "text/html", "X-CSRF-Token": this.csrfToken },
        signal: controller.signal
      })
      return response.ok ? await response.text() : null
    } catch (error) {
      if (error.name === "AbortError") return null
      throw error
    }
  }
}
